import React, { useState } from "react";
import { toast } from "react-toastify";
import { AlertNotification, colors, Input, Link, Row, Typography } from "@commonsku/styles";

import FormPopup from "../FormPopup";
import { oauth } from "../../utils";

const MANUALLY_MARK_AS_EXPORTE_POPUP_WIDTH = "640px";
const MANUALLY_MARK_AS_EXPORTE_POPUP_HEIGHT = "400px";

export type MarkExportedDocumentType =
  | "invoice"
  | "bill"
  | "credit_memo"
  | "vendor_credit";

const DOCUMENT_TYPE_LABELS: Record<MarkExportedDocumentType, string> = {
  invoice: "invoice",
  bill: "bill",
  credit_memo: "credit memo",
  vendor_credit: "vendor credit",
};

export default function ManuallyMarkAsExportedPopup({
  documentType,
  id,
  onClose,
}: {
  documentType: MarkExportedDocumentType;
  id: string;
  onClose: (saved: boolean) => void;
}) {
  const [txnId, setTxnId] = useState("");
  const [saving, setSaving] = useState(false);
  const trimmed = txnId.trim();
  const documentLabel = DOCUMENT_TYPE_LABELS[documentType];

  const onSave = async () => {
    if ("" === trimmed || saving) {
      return;
    }
    setSaving(true);
    try {
      await oauth("POST", "accounting-export", {
        actionName: "mark-exported",
        document_type: documentType,
        id,
        qbo_txn_id: trimmed,
      });
      toast.success(`Marked ${documentLabel} as exported`);
      onClose(true);
    } catch (e: unknown) {
      const errors = (e as { json?: { errors?: string[] } })?.json?.errors;
      if (Array.isArray(errors) && errors.length > 0) {
        errors.forEach((error: string) => toast.error(error));
      } else {
        toast.error("Unable to mark as exported");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormPopup
      title="Manually Mark as Exported"
      onClose={() => onClose(false)}
      onSave={onSave}
      isValid={trimmed.length > 0 && !saving}
      isNew={false}
      isEditable={true}
      width={MANUALLY_MARK_AS_EXPORTE_POPUP_WIDTH}
      height={MANUALLY_MARK_AS_EXPORTE_POPUP_HEIGHT}
    >
      <Row style={{ gap: "8px" }}>
        <AlertNotification
          alertType="warn"
          style={{ padding: "16px", marginBottom: "8px" }}
        >
          <Row>
            <Typography.Text style={{ marginBottom: "0px" }}>
              {`This action is going to manually mark this ${documentLabel} as exported without repeating the export to QuickBooks. Please enter the TxnId of the matching ${documentLabel} in QuickBooks so we can keep the paid status in sync. We do not validate the TxnId that is entered matches this ${documentLabel}. `}
              <Link
                as={"a"}
                href="https://help.commonsku.com/knowledge/export-to-quickbooks-online-qbo#duplicate"
                target="_blank"
                color={colors.primary[50]}
              >
                Learn how to find the TxnId.
              </Link>
            </Typography.Text>
          </Row>
        </AlertNotification>
        <Input
          placeholder="Enter the TxnId"
          width="50%"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={txnId || ""}
          onChange={(e) => setTxnId(e.target.value.replace(/\D/g, ""))}
        />
      </Row>
    </FormPopup>
  );
}
